import CheckIcon from "@mui/icons-material/Check";
import { Button } from "@mui/material";
import { useToast } from "@qubit/autoparts";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { NextEmptyBinSuccessAction, fetchPortStatus } from "~/redux/actions";
import { selectPortStateByPort } from "~/redux/selectors/autostoreSelectors";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useNextEmptyBinMutation } from "~/redux/warehouse/autostoreGrid.hooks";
import {
  useLazyGetWorkstationsQuery,
  useStartInductionMutation
} from "~/redux/warehouse/workstation.hooks";

import {
  setPreviousBinNumber,
  selectPreviousPutawayTask
} from "./autostorePutaway.slice";

/**
 * This button shares the same text and appearance as the ConfirmInductionButton.
 * It is used to exit the previous bin flow and resume normal induction.
 */
export function ExitPreviousBinButton() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { errorToast } = useToast();
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const portStateByPort = useAppSelector(selectPortStateByPort);
  const previousPutawayTask = useAppSelector(selectPreviousPutawayTask);

  const [startInduction] = useStartInductionMutation();
  const [getWorkstations] = useLazyGetWorkstationsQuery();
  const [getNextEmptyBin] = useNextEmptyBinMutation();

  const getIsDisabled = () => {
    if (!previousPutawayTask || !portStateByPort[previousPutawayTask.portId]) {
      return false;
    }
    return !portStateByPort[previousPutawayTask.portId].getPortResponse.isReady;
  };

  /**
   * We exit previous bin by:
   * 1. Calling start-induction
   * 2. Calling nextEmptyBin for each port
   * 3. Fetching the port status to start port polling
   */
  const handleExitPreviousBinFlow = async () => {
    if (!siteWorkstation) {
      return;
    }

    try {
      await startInduction({
        autostoreGridId: siteWorkstation.autostoreGridId,
        workstationId: siteWorkstation.id
      }).unwrap();
      await getWorkstations().unwrap();

      for (const port of siteWorkstation.ports) {
        const nextEmptyBin = await getNextEmptyBin({
          autostoreGridId: siteWorkstation.autostoreGridId,
          portId: port.portId
        }).unwrap();
        dispatch<NextEmptyBinSuccessAction>({
          type: "autostore/NEXT_EMPTY_BIN_SUCCESS",
          payload: {
            nextEmptyBin,
            portId: port.portId
          }
        });
      }

      for (const port of siteWorkstation.ports) {
        await dispatch(fetchPortStatus({ portId: port.portId }));
      }
    } catch (error) {
      errorToast("Resume induction failed", {
        description: getMessageFromRtkError(error)
      });
    }

    dispatch(setPreviousBinNumber(null));
  };

  return (
    <Button
      style={{ width: "100%" }}
      size="large"
      startIcon={<CheckIcon />}
      onClick={handleExitPreviousBinFlow}
      disabled={getIsDisabled()}
    >
      {t("confirm induction")}
    </Button>
  );
}
